/**
 * Saved-query envelope types.
 *
 * A saved query on the backend has two parts:
 *   - body:    the runnable Data(Q) script (existing /queryfile-save / -get endpoints)
 *   - sidecar: an opaque-on-the-server JSON document carrying editor-level structure
 *              (transform pipelines, builder configs) — fetched via /queryfile-meta-get
 *
 * The frontend wraps both in a single `SavedQueryEnvelope` so callers see one logical
 * object. Loaders fall back to `kind: "script"` when the sidecar is missing or unparseable
 * — the script body is always recoverable.
 */
import type {
  TransformPipeline,
  BuilderTab,
} from "types/queryBuilder.types"
import { RUN_ON_LOAD_FILE } from "./runOnLoad.types"

export type SavedQueryKind = "script" | "pipeline" | "builder" | "tabspec" | "runonload"

/** Serialized form of a transform pipeline as it lives in a sidecar. Mirrors the
 *  in-memory `TransformPipeline` exactly today; kept as its own type so the wire
 *  format can drift independently of the runtime shape. */
export interface PipelineMeta {
  kind: "pipeline"
  pipeline: TransformPipeline
}

/** Serialized form of a builder. `pipelineName` (instead of `pipelineId`) so cross-device
 *  loads can rebind to whichever local pipeline carries that name today. */
export interface BuilderMeta {
  kind: "builder"
  builder: Omit<BuilderTab, "transformPipelineId">
  pipelineName: string | null
}

export interface ScriptMeta {
  kind: "script"
}

/** A tab spec. The file body is the JSON document, not a runnable script. */
export interface TabSpecMeta {
  kind: "tabspec"
}

/** The Run on load list (types/runOnLoad.types.ts). The file body is the JSON list. */
export interface RunOnLoadMeta {
  kind: "runonload"
}

export type EnvelopeMeta = ScriptMeta | PipelineMeta | BuilderMeta | TabSpecMeta | RunOnLoadMeta

export interface SavedQueryEnvelope {
  /** Bumped when the envelope shape changes incompatibly. */
  schemaVersion: 1
  kind: SavedQueryKind
  /** The runnable Data(Q) script. Authoritative — `meta` is for the editor view. */
  script: string
  savedAt: string
  /** Hex sha-256 of `script + JSON.stringify(meta)`. Used to detect concurrent
   *  edits when two tabs save the same record without refreshing. */
  etag: string
  meta: EnvelopeMeta
  /** Human-readable name (the saved-query filename). Filled by loaders;
   *  not persisted in the sidecar (it's already the on-disk filename). */
  name: string
}

/** Bucketing of envelopes for the SavedQueries list view. */
export interface EnvelopeBuckets {
  scripts: SavedQueryEnvelope[]
  pipelines: SavedQueryEnvelope[]
  builders: SavedQueryEnvelope[]
  /** Records whose sidecar parsed but the kind/meta shape is unknown, or whose
   *  sidecar was hand-corrupted. Surface in a "Needs attention" group. */
  corrupt: SavedQueryEnvelope[]
}

/** Parameters accepted by `wrapForSave`. */
export interface WrapForSaveInput {
  script: string
  meta: EnvelopeMeta
  /** When provided, used as the etag baseline for race detection on the next
   *  save. The wrapper recomputes a fresh etag in the output regardless. */
  prevEtag?: string
}

/** Output of `wrapForSave` — what callers send to the backend. */
export interface WrapForSaveOutput {
  /** The script body — POSTed to `/queryfile-save`. */
  body: string
  /** The sidecar JSON — POSTed to `/queryfile-meta-save`. */
  sidecar: string
  /** Newly-computed etag; cache this for the next save's `prevEtag`. */
  etag: string
}

/** Tab spec files are named with this prefix so the console can leave them out by name. */
export const TAB_SPEC_FILE_PREFIX = "tabspec__"

export const isTabSpecFileName = (name: string): boolean =>
  name.startsWith(TAB_SPEC_FILE_PREFIX)

/** Saved query files the lists leave out by name: tab specs and the Run on load list. */
export const isReservedQueryFileName = (name: string): boolean =>
  isTabSpecFileName(name) || name === RUN_ON_LOAD_FILE
